import { useState } from "react";
import { Accordion, Page, Stepper } from "v2/components";
import { IBeneficiary, IFieldConfiguration, IFormConfiguration } from "./AppManualInsert.types";
import { z } from "zod";

import { AppManualInsertEdit } from "./AppManualInsert.Edit";
import { AppManualInsertSummary } from "./AppManualInsert.Summary";
import { useAlert } from "contexts/alert";
import { Label, LableTitle, Span } from "./AppManualInsert.styled";
import { cpfMask, formatCPF } from "utils/cpf";
import { formatCNPJ } from "utils/is-valid-cnpj";
import { fieldOptions } from "./AppManualInsert.utils";
import ApprovedSVG from "components/svg/approved";
import { Box, Button, IconButton, Modal } from "@mui/material";
import dayjs from "dayjs";
import { useAuth } from "contexts/auth";
import api from "services/api";
import { useParams } from "react-router-dom";
import { ArrowBack } from "@mui/icons-material";
import mockConfig from "./mockConfig.json";
import { useQuery } from "@tanstack/react-query";

// Schema Zod condicional baseado no tipo de beneficiário (PJ ou CLT)
const createBeneficiarySchema = (isCLT: boolean) => {
  const baseSchema = {
    taxPayerId: z.string().min(1, "CPF é obrigatório"),
    name: z.string().min(1, "Nome é obrigatório"),
    email: z.string().optional().nullable().transform(val => val || undefined),
    phoneNumber: z.string().optional().nullable().transform(val => val || undefined),
    race: z.string().optional().nullable().transform(val => val || undefined),
    educationLevel: z.string().optional().nullable().transform(val => val || undefined),
    nationalityCountry: z.string().optional().nullable().transform(val => val || undefined),
    birthCountry: z.string().optional().nullable().transform(val => val || undefined),
    birthState: z.string().optional().nullable().transform(val => val || undefined),
    birthCity: z.string().optional().nullable().transform(val => val || undefined),
    motherName: z.string().optional().nullable().transform(val => val || undefined),
    gender: z.string().optional().nullable().transform(val => val || undefined),
    birthDate: z.string().optional().nullable().transform(val => val || undefined),
    hiringDate: z.string().optional().nullable().transform(val => val || undefined),
    maritalStatus: z.string().optional().nullable().transform(val => val || undefined),
    weddingDate: z.string().optional().nullable().transform(val => val || undefined),
    nationalIdentityNumber: z.string().optional().nullable().transform(val => val || undefined),
    nationalIdentityEmissor: z.string().optional().nullable().transform(val => val || undefined),
    nationalIdentityExpeditionDate: z.string().optional().nullable().transform(val => val || undefined),
    zipCode: z.string().optional().nullable().transform(val => val || undefined),
    addressType: z.string().optional().nullable().transform(val => val || undefined),
    address: z.string().optional().nullable().transform(val => val || undefined),
    addressNumber: z.string().optional().nullable().transform(val => val || undefined),
    complement: z.string().optional().nullable().transform(val => val || undefined),
    neighborhood: z.string().optional().nullable().transform(val => val || undefined),
    state: z.string().optional().nullable().transform(val => val || undefined),
    city: z.string().optional().nullable().transform(val => val || undefined),
    plan: z.string().optional().nullable().transform(val => val || undefined),
    status: z.string().optional().nullable().transform(val => val || undefined),
    jobTitle: z.string().optional().nullable().transform(val => val || undefined),
    paymentFrequency: z.string().optional().nullable().transform(val => val || undefined),
    grossPayment: z.string().optional().nullable().transform(val => val || undefined),
    municipality: z.string().optional().nullable().transform(val => val || undefined),
    cbo: z.string().optional().nullable().transform(val => val || undefined),
    registrationNumber: z.string().optional().nullable().transform(val => val || undefined),
  };

  // Para PJ: CNPJ é obrigatório, para CLT: CNPJ é opcional/ignorado
  if (isCLT) {
    return z.object({
      ...baseSchema,
      legalEntityId: z.string().optional().nullable().transform(val => val || undefined),
    });
  } else {
    return z.object({
      ...baseSchema,
      legalEntityId: z.string().min(1, "CNPJ é obrigatório"),
    });
  }
};

const emailSchema = z.email();

interface IProps {
  setCurrentView: (payload: 'ADDING' | 'EDITING' | 'DEFAULT') => void;
  config: IFieldConfiguration[];
  isCLT: boolean;
}

export function AppManualInclusionMultistep({ setCurrentView, config, isCLT }: IProps) {
  const { workspaceId, workspaceActivityId } = useParams() as { workspaceId: string, workspaceActivityId: string };
  const { openAlert } = useAlert();
  const user = useAuth();

  const [editingDataError, setEditingDataError] = useState<Record<string, string>>({});
  const [beneficiaries, setBeneficiaries] = useState<IBeneficiary[]>([]);
  const [editingData, setEditingData] = useState<Partial<IBeneficiary> | null>(null);

  const [isSending, setIsSending] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [isExistingTaxPayerId, setIsExistingTaxPayerId] = useState(false);

  /** Função responsável por enviar o resultado do formulário com todos os beneficiários. */
  async function handleSubmit() {
    try {
      setIsSending(true);
      
      // Cria schema condicional baseado no tipo (PJ ou CLT)
      const beneficiarySchema = createBeneficiarySchema(isCLT);
      const beneficiariesArraySchema = z.array(beneficiarySchema).min(1, "Pelo menos um beneficiário é obrigatório");
      
      const validatedBeneficiaries = beneficiariesArraySchema.parse(beneficiaries);
      
      console.log("Dados validados:", validatedBeneficiaries);

      const data = await api.post(`/v2/apps/insert-manually/${workspaceActivityId}`, { payload: validatedBeneficiaries.map(b => ({ ...b, isCLT: isCLT }))});
      
      console.log("[API Response]", data);

      setDone(true);
    } catch (error) {
      if (error instanceof z.ZodError) {
        console.error("Erro de validação:", error);
        openAlert("Dados inválidos. Verifique se todos os campos obrigatórios estão preenchidos corretamente.", "error");
      } else {
        console.error("Erro na API:", error);
        openAlert("Algo deu errado ao inserir os valores manualmente.", "error");
      }
    } finally {
      setIsSending(false);
    }
  }

  /** Função responsável por setar um benficiário já existente para ser editado. */
  function handleEdit(id: string) {
    const beneficiary = beneficiaries.find(row => row.id === id);

    console.log(id, beneficiary)

    if (beneficiary) setEditingData(beneficiary);
  }

  /** Função responsável por adicionar um beneficiário novo para ser editado. */
  function handleAdd() {
    setEditingData({ id: String(Math.random()) });
  }

  /** Função responsável por remover um beneficiário da lista. */
  function handleDelete(taxPayerId: string) {
    setBeneficiaries(prev => prev.filter(beneficiary => beneficiary.taxPayerId !== taxPayerId));
  }

  /** Função responsável por editar um campo específico do beneficiário. */
  function handleChange(payload: Partial<IBeneficiary>) {
    setEditingData((prev) => ({ ...prev, ...payload }));
  }

  /** Função responsável por cancelar a edição ou criação sendo feita. Deve setar 'null' em 'editingData' */
  function handleEditCancel() {
    setEditingData(null);
    setEditingDataError({});
  }

  /** Função responsável por validar e salvar as alterações sendo feitas. Deve salvar em 'beneficiaries' e setar 'null' em 'editingData'. */
  async function handleEditSave() {
    try {
      /** Regras de validação. */
      let rules = {};
      const hasSpecialCharacters = /[^a-zA-Z0-9À-ÿ ]/;
      const hasLetterBetweenSpaces = /\s[a-zA-Z]\s/;
      // const alwaysMandatoryFields = ["name", "legalEntityId", "taxPayerId", "plan", "email"];
      const alwaysMandatoryFields: string[] = [];
      const ignoreFields = [(!!isCLT && "legalEntityId")];

      config.forEach(field => {
        if (!!field.visible) {
          /** Regra de campo obrigatório é aplicada conforme o que foi especificado na configuração. */
          if (!!field.required && !ignoreFields.includes(field.key) && !editingData?.[field.key]) rules = { ...rules, [field.key]: "Campo obrigatório." };

          /** Campos que sempre serão mandatórios independente da regra estabelecida. */
          if (alwaysMandatoryFields.includes(field.key) && !editingData?.[field.key]) rules = { ...rules, [field.key]: "Campo obrigatório." };

          /** Independente de ser obrigatório ou não, validações que acontecem pra campos específicos caso esteja editando eles. */
          if (field.key === "taxPayerId" && !!editingData?.taxPayerId && (editingData.taxPayerId.length !== 11 || isNaN(Number(editingData.taxPayerId)))) rules = { ...rules, ["taxPayerId"]: "CPF inválido." };
          // Validação de CNPJ só é aplicada para PJ (não CLT)
          if (!isCLT && field.key === "legalEntityId" && !!editingData?.legalEntityId && (editingData.legalEntityId.length !== 14 || isNaN(Number(editingData.legalEntityId)))) rules = { ...rules, ["legalEntityId"]: "CNPJ inválido." };
          if (field.key === "name" && !!editingData?.name && hasSpecialCharacters.test(editingData.name)) rules = { ...rules, ["name"]: "Não permitido caracteres especiais." };
          if (field.key === "name" && !!editingData?.name && hasLetterBetweenSpaces.test(editingData.name)) rules = { ...rules, ["name"]: "Não permitido abreviações." };
          if (field.key === "motherName" && !!editingData?.motherName && hasSpecialCharacters.test(editingData.motherName)) rules = { ...rules, ["motherName"]: "Não permitido caracteres especiais." };
          if (field.key === "motherName" && !!editingData?.motherName && hasLetterBetweenSpaces.test(editingData.motherName)) rules = { ...rules, ["motherName"]: "Não permitido abreviações." };
          if (field.key === "nationalIdentityNumber" && !!editingData?.nationalIdentityNumber && isNaN(Number(editingData.nationalIdentityNumber))) rules = { ...rules, ["nationalIdentityNumber"]: "RG inválido." };
          if (field.key === "email" && !!editingData?.email && !(emailSchema.safeParse(editingData.email)).success) rules = { ...rules, ["email"]: "Email inválido." };
        }
      });

      if (!!editingData?.taxPayerId) {
        const result = await api.get<{ data: boolean }>(`/v2/workspaces/${workspaceId}/beneficiaries/${editingData?.taxPayerId}/validate`);

        if (result.data.data === true) {
          rules = { ...rules, ["taxPayerId"]: "CPF já existe na base." };
          setIsExistingTaxPayerId(true);
        }
      }

      if (Object.entries(rules).length > 0) {
        setEditingDataError(rules);
        console.log("!!!", rules)
        return openAlert('Verifique se os campos preenchidos estão corretos.', 'error');
      }

      /** Importante garantir que os campos obrigatórios estão preenchidos (taxPayerId, name, email, e legalEntityId apenas para PJ) */
      setBeneficiaries([...beneficiaries.filter(beneficiary => beneficiary.id !== editingData?.id), editingData as IBeneficiary]);
      setEditingData(null);
      setEditingDataError({});
    } catch(e) {

    }
  }

  return (
    <>
      <Modal open={isExistingTaxPayerId} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
        <Box sx={{ position: "absolute" as "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", height: "auto", width: "100%", maxWidth: 500, bgcolor: "background.paper", boxShadow: 24, borderRadius: "1rem", border: "none", outline: "none", padding: "24px", display: "flex", flexDirection: "column", gap: "16px" }}>
          <h6 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Beneficiário já existente</h6>
          <span style={{ fontSize: '16px', lineHeight: '150%', fontWeight: '400', color: '#191C1A' }}>O beneficiário já existe na base de dados. Para migrar de CLT para PJ, entre em contato.</span>
          <Button
            variant='contained'
            style={{"borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","fontWeight":600,width: "100%",marginTop:"12px"}}
            onClick={() => setIsExistingTaxPayerId(false)}
          >
            Ok
          </Button>
        </Box>
      </Modal>
      {!done ? (
        <>
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "start" }}>
            {/* <IconButton color="primary" size="medium" onClick={() => setCurrentView('DEFAULT')}>
              <ArrowBack />
            </IconButton> */}
            {isCLT ? <h2>Adicionar beneficiário CLT</h2> : <h2>Adicionar beneficiário Pessoa Jurídica PJ</h2>}
          </div>
          <Stepper onSubmit={handleSubmit}>
            <Stepper.Step
              title="Beneficiário(s)"
              hideOptions={!!editingData}
              disabled={!beneficiaries.length}
              body={
                <>
                  {!editingData ? (
                    <AppManualInsertSummary beneficiaries={beneficiaries} handleAdd={handleAdd} handleEdit={handleEdit} handleDelete={handleDelete} isCLT={isCLT} />
                  ) : (
                    <AppManualInsertEdit beneficiary={editingData} beneficiaryError={editingDataError} handleChange={handleChange} handleEditSave={handleEditSave} handleEditCancel={handleEditCancel} params={config} isCLT={isCLT} />
                  )}
                </>
              }
            />
            <Stepper.Step
              title="Confirmação"
              disabled={isSending}
              body={
                <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "24px" }}>
                  <span style={{ fontSize: "16px", fontWeight: "700", color: "rgba(15, 15, 30, 0.60)" }}>{beneficiaries.length} Beneficiário(s)</span>
                  {beneficiaries.map((beneficiary) => (
                    <Accordion
                      key={beneficiary.id}
                      expanded={selected === beneficiary.id}
                      onExpand={() => selected === beneficiary.id ? setSelected(null) : setSelected(beneficiary.id)}
                      header={
                        <div style={{ width: "100%", display: "flex" }}>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", flex: "1" }}>
                            <Label>Nome completo sem abreviações</Label>
                            <LableTitle>{beneficiary.name}</LableTitle>
                          </div>
                          <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", flex: "1" }}>
                            <Label>CPF</Label>
                            <LableTitle>{cpfMask(beneficiary.taxPayerId)}</LableTitle>
                          </div>
                          {/* Só mostra CNPJ para PJ, não para CLT */}
                          {!isCLT && (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-start", flex: "1" }}>
                              <Label>CNPJ da empresa do beneficiário</Label>
                              <LableTitle>{formatCNPJ(beneficiary.legalEntityId, false)}</LableTitle>
                            </div>
                          )}
                        </div>
                      }
                    >
                      <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: "8px" }}>
                        {Object.entries(beneficiary)
                          .filter(([key]) => key !== "id")
                          // Para CLT: filtra o legalEntityId (CNPJ) da exibição
                          .filter(([key]) => isCLT ? key !== "legalEntityId" : true)
                          .map(([key, value]) => (
                          <div key={key} style={{ width: "100%", display: "flex", flexDirection: "row", justifyContent: "space-between" }}>
                            <Label>{fieldOptions.find(option => option.id === key)?.name}</Label>
                            <Span>{key === "taxPayerId" ? formatCPF(value, false) : key === "legalEntityId" ? formatCNPJ(value, false) : value}</Span>
                          </div>
                        ))}
                      </div>
                    </Accordion>
                  ))}
                </div>
              }
            />
          </Stepper>
        </>
      ) : (
        <>
          <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "30px", marginTop: "30px", marginBottom: "30px" }}>
            <ApprovedSVG />
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <h1 style={{"padding":"0","margin":"0","fontSize":"1.25rem","lineHeight":"1.75rem","fontWeight":600}}>Beneficiário(s) adicionado com sucesso</h1>
              <p style={{ fontSize: '16px', color: '#6F6F78', margin: "0", padding: "0" }}>O(s) beneficiário(s) Pessoa Jurídica foram adicionados.</p>
            </div>
            <div style={{ display: "flex", flexDirection: "row", gap: "16px" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "0px 50px" }}>
                <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>Aprovação realizada em</p>
                <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{dayjs().format("DD/MM/YYYY")}</span>
              </div>
              {user?.user?.name && (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "0px 50px" }}>
                  <p style={{ fontSize: '12px', lineHeight: '20px', color: '#6F6F78', margin: "0", padding: "0" }}>Aprovado por</p>
                  <span style={{ fontSize: '16px', lineHeight: "150%", fontWeight: '600', color: '#6F6F78', margin: "0", padding: "0" }}>{user.user.name}</span>
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: "400px", gap: "16px", marginTop: "25px" }}>
              <Button
                variant='contained'
                style={{"borderRadius":"0.375rem","height":"2.5rem","fontSize":"0.875rem","fontWeight":600,width: "100%"}}
                onClick={() => setCurrentView('DEFAULT')}
              >
                Retornar para atividade
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
